import { SizeCategory } from "../types";

/**
 * Canonical ordering for size categories (smallest to largest)
 */
const SIZE_ORDER: SizeCategory[] = [
  "very-small",
  "small",
  "midsize",
  "super-midsize",
  "large",
  "enormous",
];

/**
 * Format size category as standard abbreviated label
 *
 * This is the single source of truth for size category abbreviations.
 * All UI components should use this function to ensure consistency.
 *
 * Standard abbreviations:
 * - VS = Very Small (≤40ft)
 * - S = Small (≤60ft)
 * - M = Midsize (≤80ft)
 * - SM = Super-Midsize (≤100ft)
 * - L = Large (≤120ft)
 * - E = Enormous (>120ft)
 *
 * @param size - The canonical size category
 * @returns Abbreviated label (e.g., "SM" for "super-midsize")
 */
export function formatSizeCategory(size: SizeCategory): string {
  const labels: Record<SizeCategory, string> = {
    "very-small": "VS",
    "small": "S",
    "midsize": "M",
    "super-midsize": "SM",
    "large": "L",
    "enormous": "E",
  };
  return labels[size] || size;
}

/**
 * Sort size categories in canonical order (Very Small → Large)
 *
 * @param sizes - Array of size categories
 * @returns Sorted array (smallest to largest)
 */
export function sortSizeCategories(sizes: SizeCategory[]): SizeCategory[] {
  return [...sizes].sort((a, b) => {
    const indexA = SIZE_ORDER.indexOf(a);
    const indexB = SIZE_ORDER.indexOf(b);
    return indexA - indexB;
  });
}

/**
 * Format multiple size categories as slash-separated string
 * Categories are automatically sorted in canonical order (VS/S/M/SM/L)
 *
 * @param sizes - Array of size categories
 * @returns Abbreviated labels joined with "/" (e.g., "VS/S/M/SM/L")
 */
export function formatSizeCategories(sizes: SizeCategory[]): string {
  return sortSizeCategories(sizes).map(formatSizeCategory).join("/");
}
